import { onUnmounted, ref, unref, watch, computed } from "vue";
import type { Ref } from "vue";

type MaybeRef<T> = T | Ref<T>;
type DateInput = Date | number | string | null;

const MILLISECONDS_SECOND = 1000;
const MILLISECONDS_MINUTE = 60 * MILLISECONDS_SECOND;
const MILLISECONDS_HOUR = 60 * MILLISECONDS_MINUTE;
const MILLISECONDS_DAY = 24 * MILLISECONDS_HOUR;

export const useTimer = (dateInput: MaybeRef<DateInput>) => {
  const rawTime = ref(0);
  const targetDate = ref<Date | null>(null);
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const parseTargetDate = (raw: DateInput): Date | null => {
    if (!raw) {
      return null;
    }

    const parsedDate = raw instanceof Date ? raw : new Date(raw);

    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const updateRawTime = () => {
    const target = targetDate.value;
    if (!target) {
      rawTime.value = 0;
      return;
    }

    const now = Date.now();
    const diff = target.getTime() - now;
    rawTime.value = diff > 0 ? diff : 0;
  }

  const stop = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  const start = () => {
    if (typeof window === "undefined") {
      return;
    }

    stop();
    updateRawTime();

    if (rawTime.value === 0) return;

    intervalId = setInterval(updateRawTime, 1000);
  };

  onUnmounted(stop);

  watch(
    () => unref(dateInput),
    (value) => {
      targetDate.value = parseTargetDate(value);
      start();
    },
    { immediate: true }
  );


  const formattedTime = computed((): string => {
    if (rawTime.value <= 0) return "0h 0m 0s";

    const ms = rawTime.value;
    const days = Math.floor(ms / MILLISECONDS_DAY);
    const hours = Math.floor((ms % MILLISECONDS_DAY) / MILLISECONDS_HOUR);
    const minutes = Math.floor((ms % MILLISECONDS_HOUR) / MILLISECONDS_MINUTE);
    const seconds = Math.floor((ms % MILLISECONDS_MINUTE) / MILLISECONDS_SECOND);

    const formattedHours = String(hours).padStart(2, '0')
    const formattedMinutes = String(minutes).padStart(2, '0')
    const formattedSeconds = String(seconds).padStart(2, '0')

    return days > 0
      ? `${days}d ${formattedHours}h ${formattedMinutes}m ${formattedSeconds}s`
      : `${formattedHours}h ${formattedMinutes}m ${formattedSeconds}s`;
  });

  return {
    rawTime,
    formattedTime
  };
};
